<template>
  <HrPageLayout
    title="Departmental HR Monthly Report"
    subtitle="Recon History"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Payroll Reconciliation' },
      { label: 'Recon History' }
    ]"
  >
    <div class="recon-history-content">
      <!-- Month selector -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body hr-flex hr-align-center hr-gap-3">
          <label class="hr-form-label hr-mb-0 hr-fw-bold text-uppercase">Select Month</label>
          <select v-model="selectedMonth" class="hr-select" style="width: 200px;">
            <option value="">Select Month</option>
            <option value="01">January</option>
            <option value="02">February</option>
            <option value="03">March</option>
            <option value="04">April</option>
            <option value="05">May</option>
            <option value="06">June</option>
            <option value="07">July</option>
            <option value="08">August</option>
            <option value="09">September</option>
            <option value="10">October</option>
            <option value="11">November</option>
            <option value="12">December</option>
          </select>
        </div>
      </div>

      <!-- Stats -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon">
            <i class="bx bx-error-circle"></i>
          </div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Last Month Uploaded</p>
          <h3 class="hr-metric-value hr-text-danger">{{ summary.lastMonthUploaded ? 'Yes' : 'No' }}</h3>
          <p class="hr-text-muted hr-fs-sm hr-mt-1">Disclaimer: Data may be incomplete</p>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon">
            <i class="bx bx-error"></i>
          </div>
          <p class="hr-metric-label">Discrepancies Detected</p>
          <h3 class="hr-metric-value">{{ summary.discrepancies }}</h3>
            </div>
          <p class="hr-text-muted hr-fs-sm hr-mt-1">Last Month: {{ summary.lastDiscrepancies }}</p>
        </div>
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon">
            <i class="bx bx-group"></i>
          </div>
          <p class="hr-metric-label">Employee Count</p>
          <h3 class="hr-metric-value hr-text-danger">{{ formatNumber(summary.employeeCount) }}</h3>
          <p class="hr-text-muted hr-fs-sm hr-mt-1">Last Upload: {{ formatNumber(summary.lastEmployeeCount) }}</p>
        </div>
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon">
            <i class="bx bx-money"></i>
          </div>
          <p class="hr-metric-label">Wage Bill</p>
          <h3 class="hr-metric-value hr-text-danger">{{ summary.wageBill }}</h3>
          <p class="hr-text-muted hr-fs-sm hr-mt-1">Last Month: {{ summary.lastWageBill }}</p>
        </div>
      </div>

      <!-- Table -->
      <div class="hr-chart-card">
        <div class="hr-chart-header hr-flex hr-justify-between hr-align-center hr-flex-wrap hr-gap-2">
          <div class="hr-search-wrap">
            <div class="hr-search-box">
              <i class="bx bx-search-alt hr-search-icon"></i>
              <input
                type="text"
                v-model="search"
                class="hr-input hr-input-search"
                placeholder="Search..."
              />
            </div>
          </div>
          <div class="hr-flex hr-gap-2 hr-flex-wrap hr-align-center">
            <div class="hr-form-inline-group">
              <label class="hr-form-label hr-mb-0">Department</label>
              <select v-model="department" class="hr-select" style="min-width: 200px;">
                <option value="">All Departments</option>
                <option value="65">Finance &amp; Economic Planning</option>
                <option value="67">Governance, Administration, Communication &amp; Devolution</option>
                <option value="73">Education, Human Capital Development &amp; Vocational Training</option>
                <option value="62">Blue Economy, Fisheries, Mining &amp; Digital Economy</option>
                <option value="66">Roads, Public Works, Transport &amp; Infrastructure</option>
                <option value="61">Youth, Sports, Talent Development, Gender Inclusivity, Cultural Heritage &amp; Social Services</option>
                <option value="63">Water, Irrigation, Sanitation, Environment, Energy, Forestry &amp; Climate Change</option>
                <option value="68">Public Health &amp; Medical Services</option>
                <option value="64">Lands, Physical Planning, Housing &amp; Urban Development</option>
                <option value="69">Trade, Industry, Tourism, Co-Operative Development and Marketing</option>
                <option value="70">Agriculture and Livestock Production</option>
                <option value="72">Office of the County Secretary and Head of Public Service</option>
              </select>
            </div>
            <button type="button" class="hr-btn hr-btn-primary" @click="downloadReport">
              <i class="bx bxs-download"></i> Download Report
            </button>
          </div>
        </div>

        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table hr-table-hover hr-table-bordered">
              <thead>
                <tr>
                  <th>Date Uploaded</th>
                  <th>Month For</th>
                  <th>Total Payroll Records</th>
                  <th>Payroll Wage Bill</th>
                  <th>Employee Records</th>
                  <th>Employee Wage Bill</th>
                  <th>Employee Discrepancy Records</th>
                  <th>Wage Bill Discrepancies</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in filteredHistory" :key="idx">
                  <td>{{ item.dateUploaded }}</td>
                  <td>{{ item.monthFor }}</td>
                  <td>{{ formatNumber(item.totalPayrollRecords) }}</td>
                  <td>{{ item.payrollWageBill }}</td>
                  <td>{{ formatNumber(item.employeeRecords) }}</td>
                  <td>{{ item.employeeWageBill }}</td>
                  <td>{{ item.employeeDiscrepancyRecords }}</td>
                  <td>{{ item.wageBillDiscrepancies }}</td>
                  <td>
                    <button class="hr-btn hr-btn-sm hr-btn-primary" @click="gotTo('view_discrepancy')">
                      <i class="bx bx-show"></i> View Discrepancy
                    </button>
                  </td>
                </tr>
                <tr v-if="filteredHistory.length === 0">
                  <td colspan="9" class="hr-text-center hr-text-muted">No reconciliation history found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, executeDownload } from "@/api";

export default {
  name: "ReconHistory",
  components: { HrPageLayout },
  data() {
    return {
      search: "",
      department: "",
      selectedMonth: "",
      loading: false,
      currentPage: 1,
      totalPages: 0,
      pageSize: 10,
      summary: {
        lastMonthUploaded: false,
        discrepancies: "15",
        lastDiscrepancies: "10",
        employeeCount: 7720,
        lastEmployeeCount: 500,
        wageBill: "Ksh 15,000,000",
        lastWageBill: "Ksh 14,500,000"
      },
      history: [
        { dateUploaded: "12 Feb 2025", monthFor: "February 2025", totalPayrollRecords: 7750, payrollWageBill: "Ksh 50,000 (104.17%)", employeeRecords: 7720, employeeWageBill: "Ksh 48,000", employeeDiscrepancyRecords: 10, wageBillDiscrepancies: "Ksh 2,000" },
        { dateUploaded: "25 Apr 2023", monthFor: "April 2023", totalPayrollRecords: 7760, payrollWageBill: "Ksh 60,000 (103.45%)", employeeRecords: 7720, employeeWageBill: "Ksh 58,000", employeeDiscrepancyRecords: 10, wageBillDiscrepancies: "Ksh 2,000" },
        { dateUploaded: "15 Jun 2021", monthFor: "June 2021", totalPayrollRecords: 7740, payrollWageBill: "Ksh 55,000 (103.77%)", employeeRecords: 7720, employeeWageBill: "Ksh 53,000", employeeDiscrepancyRecords: 10, wageBillDiscrepancies: "Ksh 2,000" },
        { dateUploaded: "10 Sep 2022", monthFor: "September 2022", totalPayrollRecords: 7780, payrollWageBill: "Ksh 70,000 (102.94%)", employeeRecords: 7720, employeeWageBill: "Ksh 68,000", employeeDiscrepancyRecords: 10, wageBillDiscrepancies: "Ksh 2,000" },
        { dateUploaded: "5 Dec 2023", monthFor: "December 2023", totalPayrollRecords: 7790, payrollWageBill: "Ksh 80,000 (102.56%)", employeeRecords: 7720, employeeWageBill: "Ksh 78,000", employeeDiscrepancyRecords: 10, wageBillDiscrepancies: "Ksh 2,000" }
      ]
    };
  },
  computed: {
    filteredHistory() {
      let list = this.history;
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(
          (r) =>
            (r.dateUploaded && r.dateUploaded.toLowerCase().includes(q)) ||
            (r.monthFor && r.monthFor.toLowerCase().includes(q))
        );
      }
      if (this.department) {
        list = list.filter((r) => !r.departmentId || r.departmentId === this.department);
      }
      if (this.selectedMonth) {
        const months = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
        const monthName = months[parseInt(this.selectedMonth, 10) - 1];
        list = list.filter((r) => r.monthFor && r.monthFor.toLowerCase().includes(monthName));
      }
      return list;
    }
  },
  mounted() {
    this.fetchSummary();
  },
  methods: {
    gotTo(route) {
      this.$router.push({ name: route });
    },
    async fetchSummary() {
      try {
        const data = new FormData();
        data.append("function", "getReconHistorySummary");
        const res = await execute(data, authUrl);
        if (res?.data?.success && res?.data?.data) {
          const d = res.data.data;
          this.summary = {
            lastMonthUploaded: !!d.lastMonthUploaded,
            discrepancies: String(d.discrepancies ?? this.summary.discrepancies),
            lastDiscrepancies: String(d.lastDiscrepancies ?? this.summary.lastDiscrepancies),
            employeeCount: d.employeeCount ?? this.summary.employeeCount,
            lastEmployeeCount: d.lastEmployeeCount ?? this.summary.lastEmployeeCount,
            wageBill: d.wageBill ?? this.summary.wageBill,
            lastWageBill: d.lastWageBill ?? this.summary.lastWageBill
          };
        }
      } catch (e) {
        console.error("Error fetching recon summary:", e);
      }
    },
    formatNumber(n) {
      if (n == null) return "-";
      return Number(n).toLocaleString();
    },
    downloadReport() {
      this.$toast?.info?.("Download initiated") || alert("Download initiated");
      /* TODO: wire to executeDownload if API exists */
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.recon-history-content { padding: 0; }
.hr-fs-sm { font-size: 12px; }
.hr-mt-1 { margin-top: 4px; }
</style>
